import { FC, useMemo } from 'react'
import {
  NotificationProcessCatalogueEntry,
  useGetNotificationProcessCatalogue,
  useGetProcessOptions,
} from 'framework/api/methods'

import {
  ALL_PROCESSES,
  buildProcessFilterOptions,
  OUTCOME_FAILURE,
  OUTCOME_SUCCESS,
  OutcomeFilter,
} from '../processCatalogue'

import {
  PartnerFilterDropdown,
  PartnerFilterOption,
} from './PartnerFilterDropdown'

export { ALL_PROCESSES }

export const ALL_OUTCOMES: OutcomeFilter = ''

const OUTCOME_FILTER_OPTIONS: { value: OutcomeFilter; label: string }[] = [
  { value: ALL_OUTCOMES, label: 'All outcomes' },
  { value: OUTCOME_SUCCESS, label: 'Success' },
  { value: OUTCOME_FAILURE, label: 'Failure' },
  { value: 'Other', label: 'Reports and other' },
]

const isOutcomeFilter = (value: string): value is OutcomeFilter =>
  OUTCOME_FILTER_OPTIONS.some((option) => option.value === value)

export interface EmailAlertsFilterBarProps {
  selectedPartner: PartnerFilterOption
  onPartnerChange: (option: PartnerFilterOption) => void
  selectedProcess: string
  onProcessChange: (process: string) => void
  selectedOutcome: OutcomeFilter
  onOutcomeChange: (outcome: OutcomeFilter) => void
  searchInput: string
  onSearchChange: (value: string) => void
  onSearchClear: () => void
}

export const EmailAlertsFilterBar: FC<EmailAlertsFilterBarProps> = ({
  selectedPartner,
  onPartnerChange,
  selectedProcess,
  onProcessChange,
  selectedOutcome,
  onOutcomeChange,
  searchInput,
  onSearchChange,
  onSearchClear,
}: EmailAlertsFilterBarProps) => {
  const { data: processOptionsData } = useGetProcessOptions()
  const { data: catalogueData } = useGetNotificationProcessCatalogue()
  const descriptions: string[] = processOptionsData?.processOptions ?? []
  const catalogueEntries: NotificationProcessCatalogueEntry[] =
    catalogueData?.processes ?? []

  // Catalogue processes (one entry per Success/Failure pair) plus any recorded description the
  // catalogue does not cover, so legacy groups stay filterable (BDL20-4262).
  const processOptions = useMemo(
    () => buildProcessFilterOptions(catalogueEntries, descriptions),
    [catalogueEntries, descriptions],
  )

  return (
    <div className='flex gap-4 items-end flex-wrap mt-5'>
      <PartnerFilterDropdown
        value={selectedPartner}
        onChange={onPartnerChange}
      />

      <div className='w-[210px]'>
        <div className='text-[11.5px] font-bold text-N-500 tracking-wide mb-[6px]'>
          PROCESS
        </div>
        <select
          value={selectedProcess}
          onChange={(e) => onProcessChange(e.target.value)}
          className='w-full box-border px-3 py-[10px] border border-N-300 rounded-md text-[13.5px] text-N-800 bg-white cursor-pointer outline-none h-[41px]'>
          <option value={ALL_PROCESSES}>All processes</option>
          {processOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>

      <div className='w-[170px]'>
        <div className='text-[11.5px] font-bold text-N-500 tracking-wide mb-[6px]'>
          OUTCOME
        </div>
        <select
          value={selectedOutcome}
          onChange={(e) => {
            const { value } = e.target
            onOutcomeChange(isOutcomeFilter(value) ? value : ALL_OUTCOMES)
          }}
          className='w-full box-border px-3 py-[10px] border border-N-300 rounded-md text-[13.5px] text-N-800 bg-white cursor-pointer outline-none h-[41px]'>
          {OUTCOME_FILTER_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>

      <div className='ml-auto w-[320px]'>
        <div className='text-[11.5px] font-bold text-N-500 tracking-wide mb-[6px]'>
          SEARCH
        </div>
        <div className='relative'>
          <i className='ri-search-2-line absolute left-3 top-1/2 -translate-y-1/2 text-N-400 text-[15px]'></i>
          <input
            value={searchInput}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder='Partner, process, service name or email…'
            className='w-full box-border pl-[38px] pr-[34px] py-[10px] border border-N-300 rounded-md text-[13.5px] outline-none text-N-800 focus:border-P-500'
          />
          {searchInput && (
            <button
              type='button'
              onClick={onSearchClear}
              className='absolute right-2 top-1/2 -translate-y-1/2 w-[22px] h-[22px] flex items-center justify-center rounded-full bg-N-100 text-N-600'>
              <i className='ri-close-line text-[12px]'></i>
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

export default EmailAlertsFilterBar
